import { Chip, ChipProps, SxProps, Theme, Typography, useTheme } from "@mui/material";
import { useMemo } from "react";

import { ColorHelper } from "@/common/helpers/color";

export interface TagDisplayProps {
  label?: React.ReactNode;
  /** When set, color will be automatically picked up for the provided text. */
  pickColorForText?: string;
  /** Chip color. */
  color?: ChipProps["color"];
  backgroundColor?: string;
  disableHoverEffects?: boolean;
  onClick?: ChipProps["onClick"];
  onDelete?: ChipProps["onDelete"];
  onMouseEnter?: ChipProps["onMouseEnter"];
  onMouseLeave?: ChipProps["onMouseLeave"];
  sx?: SxProps<Theme>;
}

export default function TagDisplay({
  label,
  pickColorForText,
  color,
  backgroundColor,
  disableHoverEffects,
  onClick,
  onDelete,
  onMouseEnter,
  onMouseLeave,
  sx,
}: TagDisplayProps) {
  const theme = useTheme();

  const backgroundColorHsla = useMemo(() => {
    if (!backgroundColor) {
      return undefined;
    }
    const parsed = ColorHelper.parseCssColor(backgroundColor);
    if (!parsed.rgba) {
      return undefined;
    }
    return ColorHelper.rgbaToHsla(parsed.rgba);
  }, [backgroundColor, theme.palette.mode]);

  const colorSet = useMemo(
    () =>
      ColorHelper.getAppSoftColorSetForString({
        str: pickColorForText || "",
        isDarkTheme: theme.palette.mode === "dark",
        backgroundColorHsla: backgroundColorHsla,
      }),
    [pickColorForText, theme.palette.mode, backgroundColorHsla],
  );

  return (
    <Chip
      label={
        <Typography component='div' variant='body2'>
          {label}
        </Typography>
      }
      size='small'
      variant='filled'
      color={color}
      onClick={onClick}
      onDelete={onDelete}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        borderWidth: 1,
        borderStyle: "solid",
        // borderColor: (th) => th.palette.divider,
        border: "none",
        background: colorSet?.backgroundColorHsla.hsla,
        color: colorSet?.textColorHsla.hsl,
        // fontWeight: 500,
        "&:hover": disableHoverEffects
          ? {}
          : {
              background: colorSet?.backgroundColorHoverHsla.hsla,
              color: colorSet?.textColorHoverHsla.hsl,

              "& .MuiChip-deleteIcon": {
                color: colorSet?.textColorHoverHsla.hsl,
              },
            },
        ...sx,
      }}
    />
  );
}
